/**
 * チャット読み上げエンジン（自前・GPL非依存・Web Speech API: SpeechSynthesis）。
 *
 * 設計方針:
 * - ブラウザ・OS 非依存。Web Speech API 非対応環境では `isSupported() === false`
 *   を返し、UI 側はトグル自体を非表示にする（無音握りつぶしで例外を投げない）。
 * - デフォルトは OFF。`localStorage` キー `pool.chatTtsEnabled` で永続化。
 * - **自分の声プロファイル**（性別 + プリセット）を設定 UI から選び、`localStorage` に
 *   永続化する。プロファイルは送信時にチャットメッセージへ同梱し、相手は受信した
 *   プロファイルで読み上げる（＝「相手は相手の設定に従う」）。
 * - 受信メッセージに voice が無い場合（旧クライアント／CPU echo 等）は既定プロファイル
 *   （標準・女声）で読み上げる。
 * - 本モジュールは GPL engine に一切依存しない。
 */

const ENABLED_KEY = 'pool.chatTtsEnabled';
const GENDER_KEY = 'pool.chatTtsMeGender';
const PRESET_KEY = 'pool.chatTtsMePresetId';
const CUSTOM_PITCH_KEY = 'pool.chatTtsCustomPitch';
const CUSTOM_RATE_KEY = 'pool.chatTtsCustomRate';
const VOICE_NAME_KEY = 'pool.chatTtsMeVoiceName'; // '' or 未設定 = 自動（性別から推定）

// カスタム音声のスライダー範囲。Web Speech API 仕様のフル範囲を許容し、
// ユーザーが自由に試せるようにする。極端な値は機械的になるが、それも含めて楽しめる。
//   pitch: 仕様 0〜2.0（0 はエラー化する実装があるため 0.05 を下限）
//   rate:  仕様 0.1〜10（フルレンジ・ただし極小は聞こえない・極大は早すぎ）
export const CUSTOM_PITCH_MIN = 0.05;
export const CUSTOM_PITCH_MAX = 2.0;
export const CUSTOM_RATE_MIN = 0.1;
export const CUSTOM_RATE_MAX = 10.0;
const CUSTOM_PITCH_DEFAULT = 1.0;
const CUSTOM_RATE_DEFAULT = 1.3;

const LANG_JA = 'ja-JP';
const LANG_PREFIX_JA = 'ja';
const VOLUME = 1.0;

// 実男声ボイスを使う場合の pitch 補正係数（女声向けに低めに設定したプリセット pitch を
// そのまま実男声ボイスへ適用すると低すぎて篭るため、プリセット中央 0.85 → 実男声時 1.0 へ
// マッピングする線形変換。実男声時は voice 自体が男声なので pitch を 1.0 中心へ戻す。）
// 例: malePitch=0.85（標準） → 1.0、0.95（元気） → 1.1、0.75（落ち着き） → 0.9
const MALE_NATIVE_PITCH_BIAS = 0.15;

export type ChatTtsWho = 'me' | 'opponent';
export type Gender = 'female' | 'male';
// 'custom' = ユーザーがスライダーで自由に調整した音声プロファイル（1スロット・カスタム pitch/rate）。
export type PresetId = 'standard' | 'calm' | 'cheerful' | 'quick' | 'slow' | 'custom';

export interface VoicePreset {
  readonly id: PresetId;
  readonly label: string;
  // 性別ごとに pitch を持つ（共通テーブル＋オフセットだと男声側で差が縮こまるため独立化）。
  readonly femalePitch: number;
  readonly malePitch: number;
  readonly rate: number;
}

/** 通信メッセージに同梱する音声プロファイル（数値のみ・ブラウザ非依存）。 */
export interface VoiceProfile {
  readonly pitch: number;
  readonly rate: number;
  readonly gender: Gender;
}

// 各プリセットの数値。雰囲気の差を明瞭にするため pitch / rate のスプレッドを確保。
//   female pitch range: 0.85 〜 1.25（0.40 スプレッド）
//   male   pitch range: 0.75 〜 1.20（0.45 スプレッド・標準を高め(0.95)に再設定）
//   rate   range:       0.80 〜 1.90（1.10 スプレッド）
// 'custom' は VOICE_PRESETS に含めない（pitch/rate は別状態で保持）。
export const VOICE_PRESETS: readonly VoicePreset[] = Object.freeze([
  { id: 'standard', label: '標準', femalePitch: 1.0, malePitch: 0.95, rate: 1.3 },
  { id: 'calm', label: '落ち着き', femalePitch: 0.85, malePitch: 0.75, rate: 0.95 },
  { id: 'cheerful', label: '元気', femalePitch: 1.25, malePitch: 1.2, rate: 1.55 },
  { id: 'quick', label: '早口', femalePitch: 1.0, malePitch: 0.95, rate: 1.9 },
  { id: 'slow', label: 'ゆったり', femalePitch: 1.0, malePitch: 0.95, rate: 0.8 },
]);

export const CUSTOM_PRESET_LABEL = 'カスタム';

const DEFAULT_GENDER: Gender = 'male';
const DEFAULT_PRESET_ID: PresetId = 'standard';

/** 受信プロファイル不明時の既定（後方互換用・元気・女声）。 */
const FALLBACK_REMOTE_PROFILE: VoiceProfile = {
  pitch: VOICE_PRESETS.find((p) => p.id === 'cheerful')?.femalePitch ?? 1.1,
  rate: VOICE_PRESETS.find((p) => p.id === 'cheerful')?.rate ?? 1.4,
  gender: 'female',
};

// 高品質ボイス（Neural / Natural / Online / Premium / Enhanced / Studio / WaveNet）識別キーワード
const HIGH_QUALITY_KEYWORDS = [
  'natural',
  'neural',
  'online',
  'premium',
  'enhanced',
  'studio',
  'wavenet',
];

// 男声ボイス名キーワード（gender=male 時に優先検出）。
const MALE_VOICE_NAME_KEYWORDS = [
  'keita', 'daichi', 'naoki', 'ichiro', 'otoya', 'male',
];

const supported = (): boolean =>
  typeof window !== 'undefined' && 'speechSynthesis' in window;

const safeReadLs = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    console.warn(`chatTts: localStorage 読込失敗 (${key}):`, err);
    return null;
  }
};

const safeWriteLs = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    console.warn(`chatTts: localStorage 保存失敗 (${key}):`, err);
  }
};

const loadEnabled = (): boolean => safeReadLs(ENABLED_KEY) === '1';
const saveEnabled = (v: boolean): void => safeWriteLs(ENABLED_KEY, v ? '1' : '0');

const loadGender = (): Gender => {
  const v = safeReadLs(GENDER_KEY);
  return v === 'male' ? 'male' : DEFAULT_GENDER;
};
const saveGender = (g: Gender): void => safeWriteLs(GENDER_KEY, g);

const loadPresetId = (): PresetId => {
  const v = safeReadLs(PRESET_KEY);
  if (v === 'custom') return 'custom';
  return VOICE_PRESETS.some((p) => p.id === v) ? (v as PresetId) : DEFAULT_PRESET_ID;
};
const savePresetId = (id: PresetId): void => safeWriteLs(PRESET_KEY, id);

const loadCustomPitch = (): number => {
  const raw = safeReadLs(CUSTOM_PITCH_KEY);
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) && n >= CUSTOM_PITCH_MIN && n <= CUSTOM_PITCH_MAX
    ? n
    : CUSTOM_PITCH_DEFAULT;
};
const saveCustomPitch = (p: number): void => safeWriteLs(CUSTOM_PITCH_KEY, String(p));

const loadCustomRate = (): number => {
  const raw = safeReadLs(CUSTOM_RATE_KEY);
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) && n >= CUSTOM_RATE_MIN && n <= CUSTOM_RATE_MAX
    ? n
    : CUSTOM_RATE_DEFAULT;
};
const saveCustomRate = (r: number): void => safeWriteLs(CUSTOM_RATE_KEY, String(r));

const loadVoiceName = (): string | null => {
  const raw = safeReadLs(VOICE_NAME_KEY);
  return raw && raw.length > 0 ? raw : null; // null = 自動選定
};
const saveVoiceName = (name: string | null): void => {
  safeWriteLs(VOICE_NAME_KEY, name ?? '');
};

const scoreVoiceQuality = (v: SpeechSynthesisVoice): number => {
  const name = (v.name ?? '').toLowerCase();
  let score = 0;
  // Google 日本語を最優先（経験上最も自然）
  if (name.includes('google')) score += 100;
  for (const kw of HIGH_QUALITY_KEYWORDS) {
    if (name.includes(kw)) score += 10;
  }
  // クラウド合成（localService=false）は通常 Neural 系で自然
  if (!v.localService) score += 3;
  // ja-JP は ja-* より優先
  if ((v.lang ?? '').toLowerCase() === LANG_JA.toLowerCase()) score += 2;
  // default フラグも軽く考慮
  if (v.default) score += 1;
  return score;
};

const isMaleVoiceByName = (v: SpeechSynthesisVoice): boolean => {
  const n = (v.name ?? '').toLowerCase();
  return MALE_VOICE_NAME_KEYWORDS.some((k) => n.includes(k));
};

export interface ChatTtsEngine {
  readonly isSupported: () => boolean;
  readonly isEnabled: () => boolean;
  readonly setEnabled: (enabled: boolean) => void;
  // 「自分の声」プロファイル管理
  readonly getMyGender: () => Gender;
  readonly setMyGender: (g: Gender) => void;
  readonly getMyPresetId: () => PresetId;
  readonly setMyPresetId: (id: PresetId) => void;
  readonly getPresets: () => readonly VoicePreset[];
  // カスタム音声（1スロット・スライダーで調整）
  readonly getCustomPitch: () => number;
  readonly setCustomPitch: (p: number) => void;
  readonly getCustomRate: () => number;
  readonly setCustomRate: (r: number) => void;
  // 音声選択（明示指定／自動）。null は自動（性別から推定・現在の挙動）。
  readonly getMyVoiceName: () => string | null;
  readonly setMyVoiceName: (name: string | null) => void;
  /** 利用可能な日本語ボイス一覧（品質スコア降順）。設定UIのドロップダウン用。 */
  readonly getAvailableJaVoices: () => readonly SpeechSynthesisVoice[];
  /** 通信に同梱する自分の voice プロファイル（数値のみ）。 */
  readonly getMyVoiceProfile: () => VoiceProfile;
  /**
   * 読み上げ実行。
   * - who='me'   : 自分のメッセージ（自分のプロファイルで発声）
   * - who='opponent' : 受信メッセージ。`profile` 指定時はそれを使用（相手の設定に従う）。
   *                  未指定なら既定プロファイル（後方互換）。
   */
  readonly speak: (text: string, who: ChatTtsWho, profile?: VoiceProfile) => void;
  /** プレビュー再生（設定 UI 用・enabled に関係なく強制発声）。 */
  readonly preview: (profile: VoiceProfile, text?: string) => void;
}

export const createChatTts = (): ChatTtsEngine => {
  let enabled = supported() ? loadEnabled() : false;
  let myGender: Gender = loadGender();
  let myPresetId: PresetId = loadPresetId();
  let customPitch = loadCustomPitch();
  let customRate = loadCustomRate();
  let myVoiceName: string | null = loadVoiceName();
  // 利用可能ボイス（女声候補 / 男声候補・スコア降順）
  let femaleVoice: SpeechSynthesisVoice | null = null;
  let maleVoice: SpeechSynthesisVoice | null = null;

  const pickVoices = (): void => {
    if (!supported()) return;
    let voices: SpeechSynthesisVoice[] = [];
    try {
      voices = window.speechSynthesis.getVoices();
    } catch (err) {
      console.warn('chatTts: getVoices 失敗:', err);
      return;
    }
    const ja = voices
      .filter((v) => (v.lang ?? '').toLowerCase().startsWith(LANG_PREFIX_JA))
      .map((v) => ({ v, s: scoreVoiceQuality(v) }))
      .sort((a, b) => b.s - a.s)
      .map((x) => x.v);
    // 男声候補（名前で識別）。なければ後で pitch オフセットで代用。
    maleVoice = ja.find(isMaleVoiceByName) ?? null;
    // 女声 = 男声以外の最高スコア（実質 Google 日本語）。
    femaleVoice = ja.find((v) => !isMaleVoiceByName(v)) ?? ja[0] ?? null;
  };

  if (supported()) {
    pickVoices();
    try {
      window.speechSynthesis.onvoiceschanged = (): void => pickVoices();
    } catch (err) {
      console.warn('chatTts: onvoiceschanged 設定失敗:', err);
    }
  }

  const resolveProfile = (gender: Gender, presetId: PresetId): VoiceProfile => {
    // 'custom' は VOICE_PRESETS に含まれないため別経路でカスタム値を使う。
    if (presetId === 'custom') {
      return { pitch: customPitch, rate: customRate, gender };
    }
    const preset = VOICE_PRESETS.find((p) => p.id === presetId) ?? VOICE_PRESETS[0]!;
    const pitch = gender === 'male' ? preset.malePitch : preset.femalePitch;
    return { pitch, rate: preset.rate, gender };
  };

  const getMyVoiceProfile = (): VoiceProfile => resolveProfile(myGender, myPresetId);

  const setEnabled = (v: boolean): void => {
    enabled = v;
    saveEnabled(v);
    if (!v && supported()) {
      try {
        window.speechSynthesis.cancel();
      } catch (err) {
        console.warn('chatTts: cancel 失敗:', err);
      }
    }
  };

  /** profile に従い utter のボイスと pitch を解決する。
   *  優先順位:
   *    1) ユーザー明示選択（`myVoiceName`）が現環境に存在 → それを使う。
   *       選択ボイスが男声名キーワードに一致する場合のみ MALE_NATIVE_PITCH_BIAS を適用。
   *    2) 自動: 性別=男声 → 実男声ボイス（Keita 等）優先＋bias、無ければ女声＋低 pitch で代用。
   *    3) 自動: 性別=女声 → 女声（Google 日本語など）。 */
  const applyProfileToUtter = (
    utter: SpeechSynthesisUtterance,
    profile: VoiceProfile,
  ): void => {
    utter.lang = LANG_JA;
    let voice: SpeechSynthesisVoice | null = null;
    let pitch = profile.pitch;
    // (1) ユーザー明示選択
    if (myVoiceName && supported()) {
      try {
        const all = window.speechSynthesis.getVoices();
        const explicit = all.find((v) => v.name === myVoiceName) ?? null;
        if (explicit) {
          voice = explicit;
          // 明示選択ボイスが男声名なら男声向け pitch リベース（preset の malePitch は低めなので戻す）
          if (isMaleVoiceByName(explicit)) {
            pitch = profile.pitch + MALE_NATIVE_PITCH_BIAS;
          }
        }
      } catch (err) {
        console.warn('chatTts: explicit voice 解決失敗:', err);
      }
    }
    // (2)(3) 自動フォールバック
    if (!voice) {
      if (profile.gender === 'male') {
        if (maleVoice) {
          voice = maleVoice;
          pitch = profile.pitch + MALE_NATIVE_PITCH_BIAS;
        } else {
          voice = femaleVoice; // 女声ベース + 低 pitch で男声化
        }
      } else {
        voice = femaleVoice;
      }
    }
    if (voice) utter.voice = voice;
    // Web Speech API 仕様の範囲で clamp（カスタム値も含めてフルレンジ許容）。
    utter.pitch = Math.max(0.05, Math.min(2.0, pitch));
    utter.rate = Math.max(0.1, Math.min(10.0, profile.rate));
    utter.volume = VOLUME;
  };

  const speakInternal = (text: string, profile: VoiceProfile, who: ChatTtsWho): void => {
    if (!supported()) return;
    const trimmed = (text ?? '').trim();
    if (!trimmed) return;
    if (!femaleVoice && !maleVoice) pickVoices();
    try {
      const utter = new SpeechSynthesisUtterance(trimmed);
      applyProfileToUtter(utter, profile);
      console.info(
        `[chatTts] speak who=${who} voice="${utter.voice?.name ?? '(default)'}" ` +
          `gender=${profile.gender} pitch=${utter.pitch.toFixed(2)} rate=${utter.rate.toFixed(2)}`,
      );
      window.speechSynthesis.speak(utter);
    } catch (err) {
      console.warn('chatTts: speak 失敗:', err);
    }
  };

  const speak = (text: string, who: ChatTtsWho, profile?: VoiceProfile): void => {
    if (!enabled) return;
    if (who === 'me') {
      speakInternal(text, getMyVoiceProfile(), who);
    } else {
      speakInternal(text, profile ?? FALLBACK_REMOTE_PROFILE, who);
    }
  };

  const preview = (profile: VoiceProfile, text?: string): void => {
    // プレビューは enabled に関係なく発声（UI の試聴用）。
    // 連続切替時の発話キュー積み上がりを防ぐため、直前のプレビュー発話をキャンセルする。
    if (supported()) {
      try {
        window.speechSynthesis.cancel();
      } catch (err) {
        console.warn('chatTts: preview cancel 失敗:', err);
      }
    }
    // ⚠ Chrome / iOS Safari の既知バグ対策:
    //   `cancel()` 直後に同じタスク内で `speak()` を呼ぶと、新しい utterance まで
    //   キャンセル処理に巻き込まれて発声されない（speech-dispatcher の内部キューが flush 中のため）。
    //   `setTimeout(0..50ms)` で次のタスクに回すことで確実に発話される。
    window.setTimeout(() => {
      speakInternal(text ?? 'こんにちは', profile, 'me');
    }, 50);
  };

  return {
    isSupported: supported,
    isEnabled: (): boolean => enabled,
    setEnabled,
    getMyGender: (): Gender => myGender,
    setMyGender: (g: Gender): void => {
      myGender = g;
      saveGender(g);
    },
    getMyPresetId: (): PresetId => myPresetId,
    setMyPresetId: (id: PresetId): void => {
      myPresetId = id;
      savePresetId(id);
    },
    getPresets: (): readonly VoicePreset[] => VOICE_PRESETS,
    getCustomPitch: (): number => customPitch,
    setCustomPitch: (p: number): void => {
      const clamped = Math.max(CUSTOM_PITCH_MIN, Math.min(CUSTOM_PITCH_MAX, p));
      customPitch = clamped;
      saveCustomPitch(clamped);
    },
    getCustomRate: (): number => customRate,
    setCustomRate: (r: number): void => {
      const clamped = Math.max(CUSTOM_RATE_MIN, Math.min(CUSTOM_RATE_MAX, r));
      customRate = clamped;
      saveCustomRate(clamped);
    },
    getMyVoiceName: (): string | null => myVoiceName,
    setMyVoiceName: (name: string | null): void => {
      myVoiceName = name && name.length > 0 ? name : null;
      saveVoiceName(myVoiceName);
    },
    getAvailableJaVoices: (): readonly SpeechSynthesisVoice[] => {
      if (!supported()) return [];
      let voices: SpeechSynthesisVoice[] = [];
      try {
        voices = window.speechSynthesis.getVoices();
      } catch (err) {
        console.warn('chatTts: getVoices 失敗:', err);
        return [];
      }
      return voices
        .filter((v) => (v.lang ?? '').toLowerCase().startsWith(LANG_PREFIX_JA))
        .map((v) => ({ v, s: scoreVoiceQuality(v) }))
        .sort((a, b) => b.s - a.s)
        .map((x) => x.v);
    },
    getMyVoiceProfile,
    speak,
    preview,
  };
};

/**
 * モジュールシングルトン（controls.ts と settings.ts と main.ts の全モジュールから
 * 同じ状態を参照するため）。
 *
 * HMR 耐性化（2重防御）:
 *   (A) インスタンス本体を `window.__poolChatTtsInstance` に保持し、本モジュールが
 *       HMR で再評価されても同一インスタンスを使い続ける。状態（gender/preset/
 *       custom/voiceName 等の in-memory 値）と localStorage の同期が崩れない。
 *   (B) export は Proxy 経由とし、importer 側が握っている旧 `chatTts` 参照も
 *       常に window 上の最新インスタンスへ解決される（旧参照経由でも常に同じ
 *       singleton にアクセスできる）。
 *   (C) implementation の変更を確実に反映するため、HMR でこのモジュールが
 *       更新された場合は `accept → invalidate` でフルリロードを強制する。
 *       これにより「片方向だけ受信メッセージの voice が反映されない」等の
 *       importer 側の古いコード残存に起因する症状を防ぐ。
 *
 * テストや SSR で問題になる場合は createChatTts() を直接利用すること。
 */
declare global {
  interface Window {
    __poolChatTtsInstance?: ChatTtsEngine;
  }
}

const resolveChatTtsSingleton = (): ChatTtsEngine => {
  if (typeof window === 'undefined') {
    // SSR / 非ブラウザ環境のフォールバック（本アプリでは通常起こらない）。
    return createChatTts();
  }
  if (!window.__poolChatTtsInstance) {
    window.__poolChatTtsInstance = createChatTts();
  }
  return window.__poolChatTtsInstance;
};

export const chatTts: ChatTtsEngine = new Proxy({} as ChatTtsEngine, {
  get(_target, prop) {
    const inst = resolveChatTtsSingleton();
    const value = Reflect.get(inst, prop);
    return typeof value === 'function' ? value.bind(inst) : value;
  },
});

// (C) HMR: 本モジュール変更時はフルリロード。state + implementation の整合性を保つ。
if (import.meta.hot) {
  const hot = import.meta.hot;
  hot.accept(() => {
    // window 上のシングルトンも破棄してから無効化（新コードで作り直させる）。
    if (typeof window !== 'undefined') {
      delete window.__poolChatTtsInstance;
    }
    hot.invalidate('chat-tts: singleton needs full reload to stay consistent');
  });
}

